import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Menu,
  Switch,
  Tooltip,
  Collapse,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { components } from "../../types/api";
import SyncErrorBadge from "./SyncErrorBadge";
import {
  getPlatformIconPath,
  getPlatformDisplayName,
} from "../../utils/iconUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  useDeleteCampaignMutation,
  useUpdateCampaignMutation,
} from "../../store/apis/campaignApi";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import ConfirmationModal from "../modals/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { getStatusDisplay } from "../../utils/statusUtilts";
import { useUserRole } from "../../hooks/useUserRole";

// Use the Campaign type from the API schemas
type Campaign = components["schemas"]["CampaignWithRelations"] & {
  advertiserId?: string; // Add advertiserId property that might be needed for navigation
  errorSync?: string | null;
  lastSyncAt?: string | null;
};

type CampaignTableProps = {
  campaignsData: Campaign[];
  onSelectCampaign?: (campaign: Campaign) => void;
  onCampaignDeleted?: (campaignId: string) => void;
  onEdit?: (campaign: Campaign) => void;
  showActions?: boolean; // Control visibility of actions column
  selectedCampaignId?: string; // ID of the currently selected campaign
  renderExpandedContent?: (campaign: Campaign) => React.ReactNode; // Content rendered below an expanded campaign row
  toolbarActions?: React.ReactNode; // Extra actions (e.g. Refresh / New) shown in the sticky toolbar
  // When set, clicking a row that is already expanded runs this instead of
  // collapsing it (collapsing stays on the chevron). Lets a first click open
  // the preview and a second one drill into the campaign.
  onExpandedRowClick?: (campaign: Campaign) => void;
};

const CampaignTable: React.FC<CampaignTableProps> = ({
  campaignsData,
  onSelectCampaign,
  onCampaignDeleted,
  onEdit,
  showActions = true, // Default to showing actions
  selectedCampaignId,
  renderExpandedContent,
  toolbarActions,
  onExpandedRowClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    // offsetHeight (border box), not contentRect.height (content box): the
    // sticky header pins itself right below the toolbar, so the offset has
    // to include the toolbar's own padding and border. Measuring the content
    // box leaves the header ~23px too high, tucked under the toolbar, which
    // then paints over its top padding.
    const observer = new ResizeObserver(() => {
      setToolbarHeight(el.offsetHeight);
    });
    observer.observe(el);
    setToolbarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(
    null
  );
  const dispatch = useDispatch();
  const { isAdmin, isClient } = useUserRole();

  // --- Filtros (client-side) ---
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [briefingInput, setBriefingInput] = useState("");
  const [debouncedBriefing, setDebouncedBriefing] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [goalFilter, setGoalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput), 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedBriefing(briefingInput), 250);
    return () => clearTimeout(handle);
  }, [briefingInput]);

  // --- Menú contextual (click derecho) ---
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    campaign: Campaign;
  } | null>(null);

  // Helper to extract unique briefings from a campaign
  const getCampaignBriefings = (campaign: Campaign) => {
    const briefings = new Map<string, { id: string; name: string }>();
    if (campaign.internalCampaigns) {
      campaign.internalCampaigns.forEach((ic) => {
        if (ic.briefingAssociatedCampaigns) {
          ic.briefingAssociatedCampaigns.forEach((bac) => {
            if (bac.briefing && bac.briefing.id) {
              briefings.set(bac.briefing.id, bac.briefing);
            }
          });
        }
      });
    }
    return Array.from(briefings.values());
  };

  // Opciones de los selects derivadas de los datos presentes
  const platformOptions = useMemo(
    () =>
      Array.from(
        new Set(campaignsData.map((c) => c.platform).filter(Boolean))
      ) as string[],
    [campaignsData]
  );
  const goalOptions = useMemo(
    () =>
      Array.from(
        new Set(campaignsData.map((c) => c.goal).filter(Boolean))
      ) as string[],
    [campaignsData]
  );
  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(campaignsData.map((c) => c.status).filter(Boolean))
      ) as string[],
    [campaignsData]
  );

  const filteredCampaigns = useMemo(() => {
    const name = debouncedSearch.trim().toLowerCase();
    const briefing = debouncedBriefing.trim().toLowerCase();

    return campaignsData.filter((c) => {
      // Nombre
      if (name && !(c.displayName ?? "").toLowerCase().includes(name)) {
        return false;
      }
      // Plataforma / objetivo / status
      if (platformFilter && c.platform !== platformFilter) return false;
      if (goalFilter && c.goal !== goalFilter) return false;
      if (statusFilter && c.status !== statusFilter) return false;
      // Briefing (no aplica a CLIENT)
      if (!isClient && briefing) {
        const match = getCampaignBriefings(c).some((b) =>
          (b.name ?? "").toLowerCase().includes(briefing)
        );
        if (!match) return false;
      }
      // Fechas: la campaña debe solapar el rango [dateFrom, dateTo]
      if (dateFrom) {
        const end = c.endDate ? dayjs(c.endDate).format("YYYY-MM-DD") : null;
        if (end && end < dateFrom) return false;
      }
      if (dateTo) {
        const start = c.startDate
          ? dayjs(c.startDate).format("YYYY-MM-DD")
          : null;
        if (start && start > dateTo) return false;
      }
      return true;
    });
  }, [
    campaignsData,
    debouncedSearch,
    debouncedBriefing,
    platformFilter,
    goalFilter,
    statusFilter,
    dateFrom,
    dateTo,
    isClient,
  ]);

  const hasActiveFilters =
    !!debouncedSearch ||
    !!debouncedBriefing ||
    !!platformFilter ||
    !!goalFilter ||
    !!statusFilter ||
    !!dateFrom ||
    !!dateTo;

  const handleClearFilters = () => {
    setSearchInput("");
    setBriefingInput("");
    setPlatformFilter("");
    setGoalFilter("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const [
    deleteCampaign,
    {
      isLoading: isDeleting,
      reset: resetDeleteMutation,
      data: deleteData,
      error: deleteError,
    },
  ] = useDeleteCampaignMutation();
  const [updateCampaign] = useUpdateCampaignMutation();
  const [togglingVisibilityId, setTogglingVisibilityId] = useState<
    string | null
  >(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialized && filteredCampaigns.length > 0) {
      onSelectCampaign?.(filteredCampaigns[0]);
      const firstId = filteredCampaigns[0].id;
      if (firstId) {
        setExpandedIds((prev) => new Set(prev).add(firstId));
      }
      setInitialized(true);
    }
  }, [filteredCampaigns, initialized, onSelectCampaign]);

  // Function to close delete dialog and reset state
  const handleCloseDeleteDialog = React.useCallback(() => {
    setOpenDeleteDialog(false);
    setCampaignToDelete(null);
    resetDeleteMutation();
  }, [resetDeleteMutation]);

  // Add useEffect hooks to handle deletion data and error states
  useEffect(() => {
    if (deleteData) {
      dispatch(
        setToastMessage({
          message: t("campaigns.deleteSuccess"),
          severity: "success",
        })
      );

      // If we have a campaign to delete and it was successful, notify parent
      if (campaignToDelete?.id) {
        onCampaignDeleted?.(campaignToDelete.id);
      }

      // Reset state
      handleCloseDeleteDialog();
    }
  }, [
    deleteData,
    dispatch,
    t,
    onCampaignDeleted,
    campaignToDelete?.id,
    handleCloseDeleteDialog,
  ]);

  useEffect(() => {
    if (deleteError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.deleteError", {
            error:
              deleteError instanceof Error
                ? deleteError.message
                : "Unknown error",
          }),
          severity: "error",
        })
      );
    }
  }, [deleteError, dispatch, t]);

  const toggleExpanded = (campaignId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const handleRowClick = (campaign: Campaign) => {
    onSelectCampaign?.(campaign);
    if (!campaign.id) return;
    // Clicking an already-open row drills into the campaign rather than
    // closing it; the chevron is what collapses.
    if (expandedIds.has(campaign.id) && onExpandedRowClick) {
      onExpandedRowClick(campaign);
      return;
    }
    toggleExpanded(campaign.id);
  };

  const handleChevronClick = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation();
    if (!campaign.id) return;
    toggleExpanded(campaign.id);
  };

  // Handle delete button click
  const handleDeleteClick = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation();
    setCampaignToDelete(campaign);
    setOpenDeleteDialog(true);
  };

  const handleEditClick = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(campaign);
    } else {
      navigate(`/campaigns/${campaign.id}/edit`);
    }
  };

  const handleToggleVisibility = async (campaign: Campaign) => {
    if (!campaign.id) return;
    setTogglingVisibilityId(campaign.id);
    try {
      await updateCampaign({
        campaignId: campaign.id,
        updates: { visibleToClients: !campaign.visibleToClients },
      }).unwrap();
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "campaigns.visibilityUpdateError",
            "No se pudo actualizar la visibilidad"
          ),
          severity: "error",
        })
      );
    } finally {
      setTogglingVisibilityId(null);
    }
  };

  const handleRowContextMenu = (e: React.MouseEvent, campaign: Campaign) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, campaign });
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleOpenInNewTab = () => {
    if (contextMenu?.campaign?.id) {
      window.open(`/campaigns/${contextMenu.campaign.id}`, "_blank");
    }
    handleCloseContextMenu();
  };

  // Handle confirmation of deletion
  const handleDeleteConfirm = () => {
    console.log("Deleting campaign:", campaignToDelete);
    if (!campaignToDelete?.id) {
      console.error("No campaign ID to delete");
      handleCloseDeleteDialog();
      return;
    }

    // Just trigger the mutation, effects will be handled in useEffect hooks
    deleteCampaign(campaignToDelete.id);
  };

  // chevron + name/platform/goal/status/startDate/endDate + optional admin sync columns + actions
  const columnCount = 1 + 6 + (isAdmin ? 2 : 0) + 1;

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "12px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    position: "sticky" as const,
    top: toolbarHeight,
    // Above the sticky header cells of any nested table (MUI's stickyHeader
    // uses z-index 2), which would otherwise paint over this one by coming
    // later in the document.
    zIndex: 5,
    backgroundColor: theme.palette.background.paper,
    paddingTop: "16px",
    paddingBottom: "16px",
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:first-of-type": {
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderTopLeftRadius: 12,
      borderBottomLeftRadius: 12,
    },
    "&:last-of-type": {
      borderRight: `1px solid ${theme.palette.divider}`,
      borderTopRightRadius: 12,
      borderBottomRightRadius: 12,
    },
  };

  return (
    <>
      <Box
        ref={toolbarRef}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: theme.palette.background.paper,
          py: 1.5,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("campaigns.filters.searchPlaceholder", {
            defaultValue: "Buscar por nombre",
          })}
          sx={{ flexGrow: 1, minWidth: 200, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("campaigns.platform")}</InputLabel>
          <Select
            label={t("campaigns.platform")}
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
          >
            <MenuItem value="">
              {t("campaigns.filters.all", { defaultValue: "Todos" })}
            </MenuItem>
            {platformOptions.map((p) => (
              <MenuItem key={p} value={p}>
                {getPlatformDisplayName(p)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t("campaigns.goal")}</InputLabel>
          <Select
            label={t("campaigns.goal")}
            value={goalFilter}
            onChange={(e) => setGoalFilter(e.target.value)}
          >
            <MenuItem value="">
              {t("campaigns.filters.all", { defaultValue: "Todos" })}
            </MenuItem>
            {goalOptions.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>
            {t("campaigns.status", { defaultValue: "Status" })}
          </InputLabel>
          <Select
            label={t("campaigns.status", { defaultValue: "Status" })}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">
              {t("campaigns.filters.all", { defaultValue: "Todos" })}
            </MenuItem>
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {
                  getStatusDisplay(
                    t,
                    s as components["schemas"]["CampaignStatus"]
                  ).label
                }
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label={t("campaigns.filters.dateFrom", { defaultValue: "Desde" })}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label={t("campaigns.filters.dateTo", { defaultValue: "Hasta" })}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        {!isClient && (
          <TextField
            size="small"
            value={briefingInput}
            onChange={(e) => setBriefingInput(e.target.value)}
            placeholder={t("campaigns.filters.briefingPlaceholder", {
              defaultValue: "Buscar por briefing",
            })}
            sx={{ minWidth: 200, maxWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
        <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
          {t("campaigns.filters.results", {
            count: filteredCampaigns.length,
            defaultValue: "{{count}} resultados",
          })}
        </Typography>
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={handleClearFilters}
            sx={{ textTransform: "none" }}
          >
            {t("campaigns.filters.clear", { defaultValue: "Limpiar" })}
          </Button>
        )}
        {toolbarActions && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}
          >
            {toolbarActions}
          </Box>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          mt: 3,
          borderRadius: 3,
          overflow: "visible",
          border: "none",
          backgroundColor: "transparent",
        }}
      >
        <Table sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 40, p: 0 }} />
              <TableCell sx={headerCellSx}>
                {t("campaigns.campaignColumn", { defaultValue: "Campaign" })}
              </TableCell>
              <TableCell sx={headerCellSx}>{t("campaigns.platform")}</TableCell>
              <TableCell sx={headerCellSx}>{t("campaigns.goal")}</TableCell>
              <TableCell sx={headerCellSx}>
                {t("campaigns.status", { defaultValue: "Status" })}
              </TableCell>
              <TableCell sx={headerCellSx}>
                {t("campaigns.startDate")}
              </TableCell>
              <TableCell sx={headerCellSx}>{t("campaigns.endDate")}</TableCell>
              {isAdmin && (
                <>
                  <TableCell sx={headerCellSx}>
                    {t("campaigns.lastSyncAt", { defaultValue: "Last Sync" })}
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    {t("campaigns.errorSync", {
                      defaultValue: "Sync Error",
                    })}
                  </TableCell>
                </>
              )}
              <TableCell sx={{ ...headerCellSx, width: 100 }}>
                {t("common.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow aria-hidden sx={{ height: 20 }}>
              <TableCell
                colSpan={columnCount}
                sx={{ p: 0, border: "none", backgroundColor: "transparent" }}
              />
            </TableRow>
            {filteredCampaigns.map((campaign, index) => {
              const isSelected = Boolean(
                campaign.id && campaign.id === selectedCampaignId
              );
              const isExpanded = Boolean(
                campaign.id && expandedIds.has(campaign.id)
              );
              // When expansion is enabled, selection just tracks "last
              // clicked" and shouldn't tint the row separately from the
              // border highlight.
              const isHighlighted = renderExpandedContent ? false : isSelected;
              const cardBorderColor = isHighlighted
                ? theme.palette.primary.main
                : theme.palette.neutral[300];
              const cardBorderWidth = "2px";
              // An expanded row no longer collapses on click — it opens the
              // campaign. Tint its hover with the brand accent instead of the
              // neutral grey so that change of behaviour is visible.
              const drillsInOnClick = isExpanded && Boolean(onExpandedRowClick);
              const hoverBackground = isHighlighted
                ? theme.palette.action.selected
                : drillsInOnClick
                  ? alpha(theme.palette.secondary.main, 0.28)
                  : theme.palette.action.hover;
              return (
                <React.Fragment key={campaign.id || index}>
                  <TableRow
                    onClick={() => handleRowClick(campaign)}
                    onContextMenu={(e) => handleRowContextMenu(e, campaign)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: isHighlighted
                        ? theme.palette.action.selected
                        : theme.palette.background.paper,
                      transition: "background-color 0.3s ease",
                      "&:hover": {
                        backgroundColor: hoverBackground,
                      },
                      "& td": {
                        borderTop: `${cardBorderWidth} solid ${cardBorderColor}`,
                        borderBottom: isExpanded
                          ? "none"
                          : `${cardBorderWidth} solid ${cardBorderColor}`,
                      },
                      "& td:first-of-type": {
                        borderLeft: `${cardBorderWidth} solid ${cardBorderColor}`,
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: isExpanded ? 0 : 12,
                      },
                      "& td:last-of-type": {
                        borderRight: `${cardBorderWidth} solid ${cardBorderColor}`,
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: isExpanded ? 0 : 12,
                      },
                    }}
                  >
                    <TableCell sx={{ p: 0, textAlign: "center" }}>
                      {renderExpandedContent && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleChevronClick(e, campaign)}
                          aria-expanded={isExpanded}
                          aria-label={
                            isExpanded
                              ? t("common.collapse", {
                                  defaultValue: "Collapse",
                                })
                              : t("common.expand", { defaultValue: "Expand" })
                          }
                          sx={{
                            color: theme.palette.text.secondary,
                            transform: isExpanded
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.15s ease",
                          }}
                        >
                          <KeyboardArrowRightIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="pSmall">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexDirection: "row",
                              gap: 1,
                            }}
                          >
                            {campaign.displayName}
                          </Box>
                        </Typography>
                        {!isClient &&
                          getCampaignBriefings(campaign).length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              {getCampaignBriefings(campaign).map(
                                (briefing) => (
                                  <Chip
                                    key={briefing.id}
                                    label={briefing.name}
                                    size="small"
                                    color="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/briefings/${briefing.id}`);
                                    }}
                                    sx={{
                                      height: 20,
                                      fontSize: "0.7rem",
                                      cursor: "pointer",
                                    }}
                                  />
                                )
                              )}
                            </Box>
                          )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="pSmall">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            gap: 1,
                          }}
                        >
                          <img
                            src={getPlatformIconPath(campaign.platform)}
                            alt={getPlatformDisplayName(campaign.platform)}
                            height={24}
                            style={{ marginRight: 8 }}
                          />
                          {getPlatformDisplayName(campaign.platform)}
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="pSmallest">
                        {campaign.goal}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {campaign.status ? (
                        (() => {
                          const statusDisplay = getStatusDisplay(
                            t,
                            campaign.status
                          );
                          return (
                            <Chip
                              label={statusDisplay.label}
                              size="small"
                              color={statusDisplay.color}
                              variant="outlined"
                            />
                          );
                        })()
                      ) : (
                        <Typography variant="pSmallest" color="text.secondary">
                          {t("common.notAvailable", { defaultValue: "N/A" })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="pSmallest">
                        {campaign.startDate
                          ? dayjs(campaign.startDate).format("DD MMM YYYY")
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="pSmallest">
                        {campaign.endDate
                          ? dayjs(campaign.endDate).format("DD MMM YYYY")
                          : "-"}
                      </Typography>
                    </TableCell>
                    {isAdmin && (
                      <>
                        <TableCell>
                          <Typography variant="pSmallest">
                            {campaign.lastSyncAt
                              ? dayjs(campaign.lastSyncAt).format(
                                  "DD MMM YYYY HH:mm"
                                )
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <SyncErrorBadge error={campaign.errorSync} />
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <Box
                        sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                      >
                        {!isClient && showActions && (
                          <Tooltip
                            title={t(
                              "campaigns.visibleToClients",
                              "Visible para el cliente"
                            )}
                          >
                            <Switch
                              size="small"
                              color="success"
                              checked={!!campaign.visibleToClients}
                              disabled={togglingVisibilityId === campaign.id}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleVisibility(campaign);
                              }}
                            />
                          </Tooltip>
                        )}
                        {isAdmin && showActions && (
                          <>
                            <IconButton
                              onClick={(e) => handleEditClick(e, campaign)}
                              size="small"
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.primary.light + "20",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={(e) => handleDeleteClick(e, campaign)}
                              size="small"
                              sx={{
                                color: theme.palette.error.main,
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.error.light + "20",
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {renderExpandedContent && (
                    <TableRow>
                      <TableCell
                        sx={{
                          p: 0,
                          backgroundColor: theme.palette.background.paper,
                          border: isExpanded
                            ? `${cardBorderWidth} solid ${cardBorderColor}`
                            : "none",
                          borderTop: "none",
                          borderBottomLeftRadius: isExpanded ? 12 : 0,
                          borderBottomRightRadius: isExpanded ? 12 : 0,
                          // Own stacking context at level 0: the expanded panel
                          // renders its own tables with sticky headers, and this
                          // keeps every one of them painted below this table's
                          // sticky header regardless of their z-index.
                          position: "relative",
                          zIndex: 0,
                        }}
                        colSpan={columnCount}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              pl: { xs: 2, sm: 5 },
                              pr: 2,
                              pt: 0,
                              pb: 2,
                            }}
                          >
                            {isExpanded
                              ? renderExpandedContent(campaign)
                              : null}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow aria-hidden sx={{ height: 16 }}>
                    <TableCell
                      colSpan={columnCount}
                      sx={{
                        p: 0,
                        border: "none",
                        backgroundColor: "transparent",
                      }}
                    />
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmationModal
        open={openDeleteDialog}
        title={t("campaigns.deleteConfirmTitle")}
        message={t("campaigns.deleteConfirmMessage")}
        onConfirm={handleDeleteConfirm}
        onCancel={handleCloseDeleteDialog}
        isLoading={isDeleting}
      />

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleOpenInNewTab}>
          <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
          {t("campaigns.filters.openInNewTab", {
            defaultValue: "Abrir en nueva pestaña",
          })}
        </MenuItem>
      </Menu>
    </>
  );
};

export default CampaignTable;
